import { useEffect, useState } from 'react';

import { getApplicationState } from '../store/application/application';
import { useAppDispatch, useAppSelector } from '.';
import useArnFromPath from './useArnFromPath';

const REFRESH_STATUS_INTERVAL_NORMAL = 30000;
const REFRESH_STATUS_INTERVAL_LOADING = 2000;

const getRefreshInterval = (applicationState: string | undefined) => {
    if (
        applicationState === 'TRANSITION' ||
        applicationState === 'PROCESS_APPLICATION_SCREEN_AML' ||
        applicationState === 'PROCESS_APPLICATION_DECIDE_CREDIT'
    ) {
        return REFRESH_STATUS_INTERVAL_LOADING;
    }
    return REFRESH_STATUS_INTERVAL_NORMAL;
};

/**
 * Attempts to load the application state for the requested application
 *
 * @returns the application state
 */
const useApplicationStateRefresh = () => {
    const dispatch = useAppDispatch();
    const [isError, setIsError] = useState(false);
    const { authenticationData } = useAppSelector((state) => state.authentication);
    const applicationState = useAppSelector((state) => state.application.state);
    const applicationReferenceNumber = useArnFromPath();

    const updateState = () => {
        setIsError(false);
        if (!authenticationData || !applicationReferenceNumber) {
            return;
        }
        // LP-2568: On the consolidation page each per-card "Lagre" PATCH can make the
        // backend advance the application state (an account-only card is already
        // "complete" since KID is optional). If the background poll then applies that
        // advanced state, the user is navigated off the page mid-edit and any field
        // not yet PATCHed (e.g. a KID added after saving the account) is lost. Skip the
        // poll while on this page — the user advances explicitly via "Neste", which
        // dispatches getApplicationState directly and is not gated here.
        if (applicationState === 'COMPLEMENT_INFORMATION_CONSOLIDATIONS') {
            return;
        }
        dispatch(getApplicationState({ applicationReferenceNumber }))
            .unwrap()
            .catch(() => setIsError(true));
    };

    // Poll for state updates
    useEffect(() => {
        updateState();
        const interval = getRefreshInterval(applicationState);
        const timer = setInterval(updateState, interval);
        return () => clearInterval(timer);
    }, [applicationReferenceNumber, applicationState, authenticationData]);

    return { isError, applicationState };
};

export default useApplicationStateRefresh;
